import '@fontsource/cinzel/400.css';
import '@fontsource/cinzel/500.css';
import '@fontsource/inter-tight/300.css';
import '@fontsource/inter-tight/400.css';
import '@fontsource/inter-tight/500.css';
import '@fontsource/inter-tight/600.css';
import './print.css';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { useCustomScopes } from '../../events/EventFields';
import { scopeNames } from '../../events/fields';
import type { EventView } from '../../events/labels';
import { printModel, type PrintEvent, type PrintGroup, type PrintModel, type PrintSection, type QuotePrint } from './model';
import { paginate, type Block, type PageBlock } from './paginate';

const LOGO = '/brand/mek-logo-jasne.svg';
/** The company's lines under the logo (wzór F). */
const TAGLINE = ['Nagłośnienie wydarzeń', 'Realizacja dźwięku', 'Nagrania i mastering'];
const STATE_LABELS: Record<string, string> = { robocza: 'robocza', wyslana: 'wysłana', zaakceptowana: 'zaakceptowana', odrzucona: 'odrzucona' };

/** What of the event the user may see: each part is fetched by its own right and left out when refused. */
function usePrintEvent(eventId: string | undefined): PrintEvent {
  const quiet = { retry: false, staleTime: 30_000 } as const;
  const event = useQuery({ ...quiet, queryKey: ['events', 'one', eventId], enabled: !!eventId, queryFn: () => api<EventView>(`/api/events/${eventId}`) });
  const contractorId = event.data?.contractorId ?? null;
  const client = useQuery({ ...quiet, queryKey: ['contractors', 'one', contractorId], enabled: contractorId !== null, queryFn: () => api<{ name: string }>(`/api/contractors/${contractorId}`) });
  const { custom } = useCustomScopes(false);
  const e = event.data;
  return {
    ...(e?.name !== undefined ? { name: e.name } : {}),
    ...(e?.startsAt !== undefined ? { startsAt: e.startsAt } : {}),
    ...(e?.endsAt !== undefined ? { endsAt: e.endsAt } : {}),
    ...(e?.location !== undefined ? { location: e.location } : {}),
    ...(e?.locationAddress !== undefined ? { locationAddress: e.locationAddress } : {}),
    ...(e?.scopes !== undefined ? { scopes: scopeNames(e.scopes, custom) } : {}),
    client: client.data?.name ?? null,
  };
}

/**
 * „Podgląd wydruku” (wzór F „Sygnał”, owner 2026-10-30): the version on A4 sheets, ready for „Drukuj / zapisz PDF”
 * (the browser's print; Safari: „Zachowaj jako PDF”). It only shows the quote — everything is changed in the quote.
 */
export function QuotePrintPage() {
  const { versionId = '' } = useParams();
  const navigate = useNavigate();
  const print = useQuery({ queryKey: ['quotes', 'print', versionId], retry: false, queryFn: () => api<QuotePrint>(`/api/quotes/versions/${versionId}/print`) });
  const event = usePrintEvent(print.data?.eventId);
  const model = useMemo(() => (print.data ? printModel(print.data, event) : null), [print.data, JSON.stringify(event)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const title = model ? `Kosztorys ${model.title} (${model.number})` : 'Podgląd wydruku';
    const before = document.title;
    document.title = title;
    return () => {
      document.title = before;
    };
  }, [model]);

  const pending = print.data?.pendingPrices ?? [];
  const blocked = pending.length > 0 ? `Uzupełnij ceny pozycji własnych: ${pending.join(', ')}` : null;
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate('/wyceny'));
  return (
    <div className={`pf${blocked ? ' pf-print-blocked' : ''}`} data-feature-id="quotes.print_view">
      <div className="pf-bar">
        <button className="btn small ghost" type="button" onClick={back}>
          Wróć
        </button>
        <h1>Podgląd wydruku</h1>
        {print.data && print.data.versions.length > 1 && (
          <label>
            Wersja
            <select value={versionId} onChange={(e) => navigate(`/wydruk/kosztorys/${e.target.value}`, { replace: true })}>
              {print.data.versions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.number} · {STATE_LABELS[v.state] ?? v.state}
                </option>
              ))}
            </select>
          </label>
        )}
        {model && (
          <>
            <span className="muted">Zmiany wprowadzasz w kosztorysie.</span>
            <button className="btn primary small" type="button" onClick={() => window.print()} disabled={!!blocked} title={blocked ?? undefined}>
              Drukuj / zapisz PDF
            </button>
            {/* owner 2026-11-04 22:36: the screen is the team's preview; printing (a PDF for the client) waits for the prices */}
            {blocked && <span className="alert pf-blocked-note">{blocked}</span>}
          </>
        )}
      </div>
      {print.isLoading && (
        <p className="pf-state muted" role="status">
          Wczytywanie…
        </p>
      )}
      {print.error && (
        <p className="pf-state alert" role="alert">
          {print.error instanceof ApiError ? print.error.message : 'Nie udało się wczytać kosztorysu'}
        </p>
      )}
      {model && <Sheets m={model} />}
    </div>
  );
}

// ---------- the sheets ----------

function Logo({ small }: { small?: boolean }) {
  return <img className="pf-logo" src={LOGO} alt={small ? '' : 'MEK RECORDS'} {...(small ? { 'aria-hidden': true } : {})} />;
}

function Header({ m }: { m: PrintModel }) {
  return (
    <div className="pf-head">
      <div className="pf-brand">
        <Logo />
        <div className="pf-tagline">
          {TAGLINE.map((t, i) => (
            <Fragment key={t}>
              {i > 0 && <br />}
              {t}
            </Fragment>
          ))}
        </div>
      </div>
      <div className="pf-doc">
        <div className="pf-doc-title">KOSZTORYS</div>
        <div className="pf-doc-meta pf-num">
          {m.number}
          <br />
          {m.date}
          {m.validUntil && (
            <>
              <br />
              ważny do {m.validUntil}
            </>
          )}
        </div>
        {m.draft && <div className="pf-draft">PROJEKT</div>}
      </div>
    </div>
  );
}

function Hero({ m }: { m: PrintModel }) {
  return (
    <div className="pf-hero-block">
      <Header m={m} />
      <div className="pf-hero">
        <div>
          {m.eyebrow && <div className="pf-eyebrow">{m.eyebrow}</div>}
          <h2 className="pf-title">{m.title}</h2>
          {m.client && <div className="pf-client">{m.client}</div>}
        </div>
        {m.bigDate && (
          <div className="pf-when">
            <div className="pf-bigdate pf-num">{m.bigDate}</div>
            <div className="pf-underline" />
            {m.dateNote && <div className="pf-datenote">{m.dateNote}</div>}
          </div>
        )}
      </div>
      {m.info.length > 0 && (
        <div className="pf-info">
          {m.info.map((c) => (
            <div key={c.label}>
              <div className="pf-label">{c.label}</div>
              <div className="pf-info-value pf-num">
                {c.lines.map((line, i) => (
                  <Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                  </Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {m.structure && (
        <div className="pf-structure">
          <div className="pf-structure-head pf-label">
            <span>Struktura kosztów</span>
            <span className="pf-num">{m.structure.total}</span>
          </div>
          <div className="pf-bars" role="img" aria-label={`Struktura kosztów: ${m.structure.parts.map((p) => `${p.short} ${p.pct}`).join(', ')}`}>
            {m.structure.parts.map((p, i) => (
              <span key={i} className="pf-bar-part" style={{ flex: Math.max(p.share, 0.5), background: p.fill }} />
            ))}
          </div>
          <div className="pf-bar-labels" aria-hidden="true">
            {m.structure.parts.map((p, i, all) => (
              // the last (often a small one, e.g. transport) is right-aligned and may reach left, so it is never cut
              <span key={i} className={i === all.length - 1 && all.length > 1 ? 'pf-bar-last' : undefined} style={{ flex: Math.max(p.share, 0.5) }}>
                <span>
                  {p.short} {p.pct}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="pf-hero-end" />
    </div>
  );
}

function SectionHead({ s, continued }: { s: PrintSection; continued?: boolean | undefined }) {
  return (
    <div className="pf-sec">
      <div className="pf-sec-line">
        <span className="pf-sec-square" style={{ background: s.fill }} />
        <span className="pf-sec-title">
          {s.title}
          {continued && <small>cd.</small>}
        </span>
        <span className="pf-sec-sum pf-num">{s.sum}</span>
      </div>
    </div>
  );
}

/** „SPRZĘT” / „USŁUGI” (owner 2026-10-31): Cinzel, a thicker black rule, the small yellow bar on the right. */
function GroupHead({ g, continued }: { g: PrintGroup; continued?: boolean | undefined }) {
  return (
    <div className="pf-group">
      <span className="pf-group-title">
        {g.title}
        {continued && <small>cd.</small>}
      </span>
      <span className="pf-group-bar" aria-hidden="true" />
    </div>
  );
}

/** Under a group: „Razem sprzęt”, „Rabat 10%”, „Sprzęt po rabacie” (right-aligned). */
function GroupSummary({ g }: { g: PrintGroup }) {
  return (
    <div className="pf-group-sum pf-num">
      {g.summary.map((r) => (
        <Fragment key={r.label}>
          <span className={r.strong ? 'pf-strong' : 'pf-totals-label'}>{r.label}</span>
          <span className={r.strong ? 'pf-strong pf-totals-amount' : 'pf-totals-amount'}>{r.amount}</span>
        </Fragment>
      ))}
    </div>
  );
}

function Row({ r }: { r: PrintSection['rows'][number] }) {
  return (
    <div className="pf-row">
      <span />
      <span>
        <span className="pf-row-name">{r.name}</span>
        {r.desc && <span className="pf-row-desc"> · {r.desc}</span>}
      </span>
      <span className="pf-row-qty pf-num">{r.qty}</span>
      <span className="pf-row-price pf-num">{r.price}</span>
      <span className="pf-row-value pf-num">{r.value}</span>
    </div>
  );
}

function End({ m }: { m: PrintModel }) {
  return (
    <div className="pf-end">
      <div className="pf-terms">
        {m.terms && (
          <>
            <div className="pf-label">Warunki</div>
            {m.terms}
          </>
        )}
      </div>
      {m.totals && (
        <div className="pf-totals pf-num">
          {m.totals.rows.map((r) => (
            <Fragment key={r.label}>
              <span className={r.big ? 'pf-totals-label pf-totals-big' : 'pf-totals-label'}>{r.label}</span>
              <span className={r.big ? 'pf-totals-amount pf-totals-big' : 'pf-totals-amount'}>{r.amount}</span>
            </Fragment>
          ))}
          <div className="pf-rule" aria-hidden="true">
            <span />
            <span />
          </div>
          <span className="pf-pay">DO ZAPŁATY</span>
          <span className="pf-gross">{m.totals.gross}</span>
        </div>
      )}
    </div>
  );
}

function Footer({ m, page, pages }: { m: PrintModel; page: number; pages: number }) {
  return (
    <div className="pf-foot">
      <span>
        {m.company.map((line, i) => (
          <Fragment key={i}>
            {i > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </span>
      <span style={{ display: 'flex', alignItems: 'flex-end' }}>
        {pages > 1 && (
          <span className="pf-pageno">
            strona {page} z {pages}
          </span>
        )}
        <span className="pf-mark" aria-hidden="true">
          <span className="hair" />
          <span className="hair" />
          <span className="hair" />
          <span className="wide" />
          <span className="bar" />
          <span className="bar" />
        </span>
      </span>
    </div>
  );
}

function Slim({ m, page, pages }: { m: PrintModel; page: number; pages: number }) {
  return (
    <div className="pf-slim">
      <Logo small />
      <span className="pf-slim-meta pf-num">
        <span className="pf-cinzel">KOSZTORYS</span>
        {m.number}
        {m.title && ` · ${m.title}`} · strona {page} z {pages}
      </span>
    </div>
  );
}

type Item = Block & { node: (continued?: boolean) => ReactNode };

/** The blocks of the sheets (exported for the tests: the pages' rules). */
export function itemsOf(m: PrintModel): Item[] {
  const out: Item[] = [{ key: 'hero', kind: 'hero', height: 0, node: () => <Hero m={m} /> }];
  // the PDF's order (modules/quotes `pdf.ts`): a group's head goes with its first section's head
  let si = 0;
  m.groups.forEach((g, gi) => {
    g.sections.forEach((s, i) => {
      const sec = si++;
      const head = (continued?: boolean) =>
        g.list ? (
          <GroupHead g={g} continued={continued} />
        ) : continued || i > 0 ? (
          <SectionHead s={s} continued={continued} />
        ) : (
          <>
            <GroupHead g={g} />
            <SectionHead s={s} />
          </>
        );
      out.push({ key: `h${sec}`, kind: 'head', section: sec, height: 0, node: head });
      s.rows.forEach((r, ri) => out.push({ key: `r${sec}.${ri}`, kind: 'row', section: sec, height: 0, node: () => <Row r={r} /> }));
    });
    // review: the sums never open a page bare — they belong to the group's last section (its head „cd.” above them)
    out.push({ key: `g${gi}`, kind: 'row', ...(g.sections.length > 0 ? { section: si - 1 } : {}), height: 0, node: () => <GroupSummary g={g} /> });
  });
  out.push({ key: 'end', kind: 'totals', height: 0, node: () => <End m={m} /> });
  return out;
}

/** The sheet's inner height (297 mm less the paddings 52 + 44 px). */
const SHEET_PX = (297 / 25.4) * 96 - 52 - 44;

/** Measures every block once in a hidden sheet of the same width, then lays the blocks out on A4 sheets. */
function Sheets({ m }: { m: PrintModel }) {
  const items = useMemo(() => itemsOf(m), [m]);
  const [pages, setPages] = useState<PageBlock[][] | null>(null);
  const [fontsTick, setFontsTick] = useState(0);
  const measure = useRef<HTMLDivElement>(null);
  useEffect(() => setPages(null), [items, fontsTick]);
  useEffect(() => {
    let live = true;
    // measured again with the real fonts (Cinzel and Inter Tight change the heights)
    document.fonts?.ready.then(() => live && setFontsTick((t) => t + 1));
    return () => {
      live = false;
    };
  }, []);
  useLayoutEffect(() => {
    if (pages !== null || !measure.current) return;
    const box = measure.current;
    const h = (sel: string) => (box.querySelector(sel) as HTMLElement | null)?.getBoundingClientRect().height ?? 0;
    const heights = new Map<string, number>();
    box.querySelectorAll<HTMLElement>('[data-block]').forEach((el) => heights.set(el.dataset.block!, el.getBoundingClientRect().height));
    const foot = h('.pf-foot');
    const slim = h('.pf-slim');
    const blocks = items.map(({ key, kind, section }) => ({ key, kind, height: heights.get(key) ?? 0, ...(section !== undefined ? { section } : {}) }));
    // 2 px of slack for rounding between screen and print
    setPages(paginate(blocks, SHEET_PX - foot - 2, SHEET_PX - foot - slim - 2));
  }, [pages, items]);

  const byKey = new Map(items.map((i) => [i.key, i]));
  if (pages === null) {
    return (
      <div className="pf-measure" ref={measure} aria-hidden="true">
        <div className="pf-sheet">
          <Slim m={m} page={2} pages={2} />
          <div className="pf-content">
            {items.map((i) => (
              <div key={i.key} data-block={i.key}>
                {i.node(i.kind === 'head')}
              </div>
            ))}
          </div>
          <Footer m={m} page={1} pages={2} />
        </div>
      </div>
    );
  }
  return (
    <div className="pf-desk">
      {pages.map((page, pi) => (
        <section key={pi} className="pf-sheet" aria-label={`Strona ${pi + 1} z ${pages.length}`}>
          {pi > 0 && <Slim m={m} page={pi + 1} pages={pages.length} />}
          <div className="pf-content">
            {page.map((b) => (
              <Fragment key={`${b.key}${b.continued ? '+' : ''}`}>{byKey.get(b.key)!.node(b.continued)}</Fragment>
            ))}
          </div>
          <Footer m={m} page={pi + 1} pages={pages.length} />
        </section>
      ))}
    </div>
  );
}
